import React, { useState, useContext, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TextInput,
  TouchableOpacity,
  ScrollView,
  Image,
  Switch,
  Platform,
  ActivityIndicator
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { Ionicons } from '@expo/vector-icons';
import { AuthContext } from '../contexts/AuthContext';
import { SheetContext } from '../contexts/SheetContext';
import { config } from '../config';
import alertService from '../services/alertService';
import hapticService, { HAPTIC_TYPES } from '../services/hapticService';
import sheetService from '../services/sheetService';
import LoadingScreen from '../components/LoadingScreen';
import AsyncStorageHelper from '../services/asyncStorageHelper';

const SettingsScreen = ({ navigation }) => {
  const { 
    user, 
    signOut, 
    sessionInfo, 
    sessionTimeRemaining, 
    getSessionDetails,
    checkSession,
    getJWTPayload,
    isJWTValid,
    isUserAdmin, // 🆕 Fonction admin
    clearServerCache // 🆕 Fonction clear cache serveur
  } = useContext(AuthContext);
  const { 
    testConnection, 
    isLoading,
    // 🆕 Nouvelles propriétés pour les onglets cachés
    showHiddenSheets,
    hiddenSheetsCount,
    toggleShowHiddenSheets,
    canManageHiddenSheets
  } = useContext(SheetContext);
  
  const [isTesting, setIsTesting] = useState(false);
  const [hapticEnabled, setHapticEnabled] = useState(hapticService.getEnabled());
  const [hapticType, setHapticType] = useState(hapticService.getType());
  const [detailedSessionInfo, setDetailedSessionInfo] = useState(null);
  const [loadingSessionInfo, setLoadingSessionInfo] = useState(false);
  const [clearingCache, setClearingCache] = useState(false);
  const [clearingServerCache, setClearingServerCache] = useState(false); // 🆕 État pour cache serveur
  const [clearingSectionMemory, setClearingSectionMemory] = useState(false); // 🆕 État pour effacer section
  const [togglingHiddenSheets, setTogglingHiddenSheets] = useState(false); // 🆕 État pour onglets cachés
  
  const [jwtPayload, setJwtPayload] = useState(null);
  const [cleaningSession, setCleaningSession] = useState(false);
  const [validatingToken, setValidatingToken] = useState(false);
  
  // 🆕 État pour section dépliante
  const [isAdvancedExpanded, setIsAdvancedExpanded] = useState(false);

  useEffect(() => {
    const loadHapticSettings = async () => {
      await hapticService.loadSettings();
      setHapticEnabled(hapticService.getEnabled());
      setHapticType(hapticService.getType());
    };
    loadHapticSettings();
  }, []);

  useEffect(() => {
    if (user && user.token) {
      const payload = getJWTPayload(user.token);
      setJwtPayload(payload);
    }
  }, [user, getJWTPayload]);

  // 🆕 Auto-refresh session info on load
  useEffect(() => {
    const autoRefreshSessionInfo = async () => {
      if (user && getSessionDetails) {
        try {
          await loadSessionDetails();
        } catch (error) {
          console.log('Auto-refresh session info failed:', error);
        }
      }
    };

    // Auto-refresh après un petit délai
    const timer = setTimeout(autoRefreshSessionInfo, 1000);
    return () => clearTimeout(timer);
  }, [user]);

  const loadSessionDetails = async () => {
    setLoadingSessionInfo(true);
    try {
      const details = await getSessionDetails();
      setDetailedSessionInfo(details);
      hapticService.success();
    } catch (error) {
      hapticService.error();
      alertService.showAlert('Error', 'Failed to load session details');
    } finally {
      setLoadingSessionInfo(false);
    }
  };

  const validateToken = async () => {
    setValidatingToken(true);
    try {
      if (!user || !user.token) {
        alertService.showAlert('No Token', 'No JWT token found');
        return;
      }

      const isValid = isJWTValid(user.token);
      const payload = getJWTPayload(user.token);
      const now = Math.floor(Date.now() / 1000);
      
      const report = {
        valid: isValid,
        expired: payload.exp < now,
        timeRemaining: Math.max(0, payload.exp - now),
        issuer: payload.iss,
        deviceId: payload.device_id,
        sessionId: payload.session_id,
        isAdmin: payload.isAdmin || false // 🆕 Statut admin
      };

      hapticService.success();
      alertService.showAlert(
        'Token Validation',
        `Valid: ${report.valid ? 'Yes' : 'No'}\nExpired: ${report.expired ? 'Yes' : 'No'}\nTime Remaining: ${Math.floor(report.timeRemaining / 60)} minutes\nAdmin: ${report.isAdmin ? 'Yes' : 'No'}\nDevice ID: ${report.deviceId.substring(0, 8)}...\nSession ID: ${report.sessionId.substring(0, 12)}...`
      );
    } catch (error) {
      hapticService.error();
      alertService.showAlert('Error', 'Failed to validate token: ' + error.message);
    } finally {
      setValidatingToken(false);
    }
  };

  const cleanSession = async () => {
    setCleaningSession(true);
    
    alertService.showAlert(
      'Clean Session Data',
      'This will remove all stored session data and force a fresh login. Continue?',
      [
        { 
          text: 'Cancel', 
          style: 'cancel',
          onPress: () => {
            setCleaningSession(false);
            hapticService.button();
          }
        },
        { 
          text: 'Clean & Logout', 
          style: 'destructive',
          onPress: async () => {
            try {
              await Promise.all([
                AsyncStorageHelper.removeItem('jwtToken'),
                AsyncStorageHelper.removeItem('userEmail'),
                AsyncStorageHelper.removeItem('sheetId'),
                AsyncStorageHelper.removeItem('isAdmin'),
                AsyncStorageHelper.removeItem('competitions'),
                AsyncStorageHelper.removeItem('lastSelectedSection'),
                AsyncStorageHelper.removeItem('hapticType'),
                AsyncStorageHelper.removeItem('hapticEnabled'),
                AsyncStorageHelper.removeItem('refreshInterval'),
                AsyncStorageHelper.removeItem('navigationTransition'),
                AsyncStorageHelper.removeItem('pwaInstallDismissed'),
                AsyncStorageHelper.removeItem('pwaInstallLater'),
                AsyncStorageHelper.removeItem('showHiddenSheets') // 🆕 Nettoyer préférence onglets cachés
              ]);

              sheetService.clearCache();

              hapticService.success();
              
              alertService.showAlert(
                'Session Cleaned', 
                'All session data has been cleared. You will be redirected to login.',
                [{ 
                  text: 'OK',
                  onPress: async () => {
                    await signOut();
                    navigation.reset({
                      index: 0,
                      routes: [{ name: 'Login' }]
                    });
                  }
                }]
              );
            } catch (error) {
              console.error('Error cleaning session:', error);
              hapticService.error();
              alertService.showAlert('Error', 'Failed to clean session data');
            } finally {
              setCleaningSession(false);
            }
          }
        }
      ]
    );
  };

  // 🆕 Fonction pour effacer la mémorisation de section
  const clearSectionMemory = async () => {
    setClearingSectionMemory(true);
    hapticService.button();
    
    alertService.showAlert(
      'Clear Section Memory',
      'This will clear your assigned section memory. You will need to select your section again when scoring.\n\nAre you sure?',
      [
        { 
          text: 'Cancel', 
          style: 'cancel',
          onPress: () => {
            setClearingSectionMemory(false);
            hapticService.button();
          }
        },
        { 
          text: 'Clear Memory', 
          onPress: async () => {
            try {
              const success = await AsyncStorageHelper.removeItem('lastSelectedSection');
              
              if (success) {
                hapticService.success();
                alertService.showAlert(
                  'Section Memory Cleared', 
                  'Your assigned section memory has been cleared successfully. You will need to select your section again when scoring.',
                  [{ 
                    text: 'OK',
                    onPress: () => hapticService.button()
                  }]
                );
              } else {
                throw new Error('Failed to remove section memory');
              }
            } catch (error) {
              console.error('Error clearing section memory:', error);
              hapticService.error();
              alertService.showAlert('Error', 'Failed to clear section memory. Please try again.');
            } finally {
              setClearingSectionMemory(false);
            }
          }
        }
      ]
    );
  };

  // 🆕 Fonction pour basculer l'affichage des onglets cachés
  const handleToggleHiddenSheets = async () => {
    if (!canManageHiddenSheets) {
      alertService.showAlert('Access Denied', 'Admin privileges required for hidden sheets management.');
      return;
    }

    hapticService.button();
    setTogglingHiddenSheets(true);
    
    try {
      const success = await toggleShowHiddenSheets();
      
      if (success) {
        hapticService.success();
        alertService.showAlert(
          'Hidden Sheets Setting Updated',
          `Hidden sheets are now ${!showHiddenSheets ? 'visible' : 'hidden'}. ${hiddenSheetsCount} hidden sheet${hiddenSheetsCount !== 1 ? 's' : ''} available.`,
          [{ 
            text: 'OK',
            onPress: () => hapticService.button()
          }]
        );
      } else {
        throw new Error('Failed to update setting');
      }
    } catch (error) {
      console.error('Error toggling hidden sheets:', error);
      hapticService.error();
      alertService.showAlert('Error', 'Failed to update hidden sheets setting. Please try again.');
    } finally {
      setTogglingHiddenSheets(false);
    }
  };

  const copyDebugInfo = async () => {
    if (!user || !jwtPayload) {
      alertService.showAlert('No Data', 'No session data available');
      return;
    }

    const debugInfo = {
      email: user.email,
      isAdmin: user.isAdmin, // 🆕 Inclure statut admin
      tokenType: 'JWT',
      deviceId: jwtPayload.device_id,
      sessionId: jwtPayload.session_id,
      issuedAt: new Date(jwtPayload.iat * 1000).toISOString(),
      expiresAt: new Date(jwtPayload.exp * 1000).toISOString(),
      issuer: jwtPayload.iss,
      sheetId: jwtPayload.sheetId,
      timeRemaining: Math.max(0, jwtPayload.exp - Math.floor(Date.now() / 1000)),
      appVersion: config.APP_VERSION,
      platform: Platform.OS,
      showHiddenSheets: showHiddenSheets, // 🆕 Inclure préférence onglets cachés
      hiddenSheetsCount: hiddenSheetsCount
    };

    const debugText = JSON.stringify(debugInfo, null, 2);

    if (typeof navigator !== 'undefined' && navigator.clipboard) {
      try {
        await navigator.clipboard.writeText(debugText);
        hapticService.success();
        alertService.showAlert('Copied', 'Debug info copied to clipboard');
      } catch (error) {
        alertService.showAlert('Debug Info', debugText);
      }
    } else {
      alertService.showAlert('Debug Info', debugText);
    }
  };

  const handleHapticEnabledChange = async (enabled) => {
    setHapticEnabled(enabled);
    await hapticService.saveSettings(hapticType, enabled);
    if (enabled) {
      hapticService.test();
    }
  };

  const handleHapticTypeChange = async (type) => {
    setHapticType(type);
    await hapticService.saveSettings(type, hapticEnabled);
    if (hapticEnabled) {
      hapticService.test();
    }
  };

  const handleTestConnection = async () => {
    hapticService.button();
    setIsTesting(true);
    try {
      const success = await testConnection(config.GOOGLE_SHEET_ID);
      if (success) {
        hapticService.success();
        alertService.showAlert('Success', 'Connection test successful');
      } else {
        hapticService.error();
      }
    } finally {
      setIsTesting(false);
    }
  };

  // Cache client
  const handleClearCache = async () => {
    hapticService.button();
    
    alertService.showAlert(
      'Clear Client Cache',
      'This will clear all cached data (races, pilots, sections) stored locally. The app will reload fresh data from server cache.\n\nAre you sure?',
      [
        { 
          text: 'Cancel', 
          style: 'cancel',
          onPress: () => hapticService.button()
        },
        { 
          text: 'Clear Cache', 
          style: 'destructive',
          onPress: async () => {
            setClearingCache(true);
            try {
              sheetService.clearCache();
              
              hapticService.success();
              alertService.showAlert(
                'Client Cache Cleared', 
                'Local cache has been cleared successfully. Fresh data will be loaded on next refresh.',
                [{ 
                  text: 'OK',
                  onPress: () => hapticService.button()
                }]
              );
            } catch (error) {
              console.error('Error clearing cache:', error);
              hapticService.error();
              alertService.showAlert('Error', 'Failed to clear cache. Please try again.');
            } finally {
              setClearingCache(false);
            }
          }
        }
      ]
    );
  };

  // 🆕 Cache serveur (admin seulement)
  const handleClearServerCache = async () => {
    if (!isUserAdmin()) {
      alertService.showAlert('Access Denied', 'Admin privileges required for server cache operations.');
      return;
    }

    hapticService.button();
    
    alertService.showAlert(
      'Clear Server Cache (Admin)',
      'This will clear ALL cached data on the server (races, pilots, sections) for ALL users. Everyone will reload fresh data from Google Sheets.\n\nThis is a GLOBAL operation. Continue?',
      [
        { 
          text: 'Cancel', 
          style: 'cancel',
          onPress: () => hapticService.button()
        },
        { 
          text: 'Clear Server Cache', 
          style: 'destructive',
          onPress: async () => {
            setClearingServerCache(true);
            try {
              const result = await clearServerCache();
              
              hapticService.success();
              alertService.showAlert(
                'Server Cache Cleared', 
                `Server cache cleared successfully!\n\nDeleted files: ${result.deletedFiles}\n\nAll users will now load fresh data from Google Sheets.`,
                [{ 
                  text: 'OK',
                  onPress: () => hapticService.button()
                }]
              );
            } catch (error) {
              console.error('Error clearing server cache:', error);
              hapticService.error();
              
              if (error.message.includes('Admin access required')) {
                alertService.showAlert('Access Denied', 'Admin privileges required for this operation.');
              } else {
                alertService.showAlert('Error', `Failed to clear server cache: ${error.message}`);
              }
            } finally {
              setClearingServerCache(false);
            }
          }
        }
      ]
    );
  };
  
  const handleSignOut = () => {
    hapticService.button();
    alertService.showAlert(
      'Sign Out',
      'Are you sure you want to sign out?',
      [
        { text: 'Cancel', style: 'cancel' },
        { 
          text: 'Sign Out', 
          onPress: async () => {
            hapticService.button();
            await signOut();
            navigation.reset({
              index: 0,
              routes: [{ name: 'Login' }]
            });
          },
          style: 'destructive'
        }
      ]
    );
  };

  const handleHelpPress = () => {
    hapticService.button();
    navigation.navigate('Help');
  };

  const getHapticTypeName = (type) => {
    switch (type) {
      case HAPTIC_TYPES.DISABLED:
        return 'Disabled';
      case HAPTIC_TYPES.LIGHT:
        return 'Light';
      case HAPTIC_TYPES.MEDIUM:
        return 'Medium';
      case HAPTIC_TYPES.HEAVY:
        return 'Strong';
      case HAPTIC_TYPES.SOUND:
        return 'Sound';
      default:
        return 'Medium';
    }
  };

  // 🆕 Toggle advanced section
  const toggleAdvancedSection = () => {
    hapticService.button();
    setIsAdvancedExpanded(!isAdvancedExpanded);
  };
  
  return (
    <SafeAreaView style={styles.container} edges={['bottom']}>
      <ScrollView 
        contentContainerStyle={styles.contentContainer}
        showsVerticalScrollIndicator={true}
        bounces={true}
      >

        {/* 1. HAPTIC FEEDBACK */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Haptic Feedback</Text>
          
          <View style={styles.settingRow}>
            <View style={styles.settingInfo}>
              <Text style={styles.settingLabel}>Enable Feedback</Text>
              <Text style={styles.settingDescription}>
                Vibrate or play sound when interacting with the app
              </Text>
            </View>
            <Switch
              value={hapticEnabled}
              onValueChange={handleHapticEnabledChange}
              trackColor={{ false: '#e0e0e0', true: '#0066cc' }}
              thumbColor={hapticEnabled ? '#fff' : '#f4f3f4'}
            />
          </View>
          
          {hapticEnabled && (
            <View style={styles.hapticTypes}>
              <Text style={styles.settingLabel}>Feedback Type</Text>
              <View style={styles.hapticButtonsGrid}>
                {Object.values(HAPTIC_TYPES).filter(type => type !== HAPTIC_TYPES.DISABLED).map((type) => (
                  <TouchableOpacity
                    key={type}
                    style={[
                      styles.hapticButton,
                      hapticType === type && styles.hapticButtonSelected
                    ]}
                    onPress={() => handleHapticTypeChange(type)}
                  >
                    <Text
                      style={[
                        styles.hapticButtonText,
                        hapticType === type && styles.hapticButtonTextSelected
                      ]}
                    >
                      {getHapticTypeName(type)}
                    </Text>
                  </TouchableOpacity>
                ))}
              </View>
              
              <View style={styles.noteContainer}>
                <Ionicons name="information-circle-outline" size={16} color="#0066cc" />
                <Text style={styles.noteText}>
                  {Platform.OS === 'ios' 
                    ? "Sound feedback uses system notification sounds" 
                    : "Sound feedback is only available on iOS devices"}
                </Text>
              </View>
            </View>
          )}
        </View>

        {/* 2. ACCOUNT */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Account</Text>
          
          {user && (
            <View style={styles.userInfo}>
              {user.picture && (
                <Image source={{ uri: user.picture }} style={styles.userAvatar} />
              )}
              <View style={styles.userDetails}>
                <Text style={styles.userName}>{user.name || 'User'}</Text>
                <Text style={styles.userEmail}>{user.email}</Text>
                {user.isAdmin && (
                  <Text style={styles.adminLabel}>Administrator</Text>
                )}
              </View>
            </View>
          )}

          {/* 🆕 Section Memory Management */}
          <View style={styles.sectionMemoryContainer}>
            <View style={styles.settingInfo}>
              <Text style={styles.settingLabel}>Section Assignment Memory</Text>
              <Text style={styles.settingDescription}>
                Clear your assigned section memory to reselect it when scoring
              </Text>
            </View>
            
            <TouchableOpacity
              style={styles.clearSectionButton}
              onPress={clearSectionMemory}
              disabled={clearingSectionMemory}
            >
              {clearingSectionMemory ? (
                <View style={styles.clearSectionLoading}>
                  <ActivityIndicator color="#fff" size="small" />
                  <Text style={styles.clearSectionButtonText}>Clearing...</Text>
                </View>
              ) : (
                <View style={styles.clearSectionButtonContent}>
                  <Ionicons name="refresh-outline" size={20} color="#fff" />
                  <Text style={styles.clearSectionButtonText}>Clear Section Memory</Text>
                </View>
              )}
            </TouchableOpacity>
          </View>
          
          <TouchableOpacity
            style={styles.signOutButton}
            onPress={handleSignOut}
          >
            <Text style={styles.signOutButtonText}>Sign Out</Text>
          </TouchableOpacity>
        </View>

        {/* 🆕 3. ADVANCED SETTINGS (Section dépliante) */}
        <View style={styles.section}>
          <TouchableOpacity
            style={styles.advancedHeader}
            onPress={toggleAdvancedSection}
          >
            <Text style={styles.advancedTitle}>
              System & Debug Settings
              {user?.isAdmin && <Text style={styles.adminBadge}>ADMIN</Text>}
            </Text>
            <Ionicons 
              name={isAdvancedExpanded ? "chevron-up" : "chevron-down"} 
              size={24} 
              color="#0066cc" 
            />
          </TouchableOpacity>

          {isAdvancedExpanded && (
            <View style={styles.advancedContent}>
              
              {/* 🆕 HIDDEN SHEETS MANAGEMENT (Admin seulement) */}
              {canManageHiddenSheets && (
                <View style={styles.subSection}>
                  <Text style={styles.subSectionTitle}>Hidden Sheets Management</Text>
                  
                  <Text style={styles.hiddenSheetsDescription}>
                    Control visibility of hidden Google Sheets tabs in the races list. 
                    {hiddenSheetsCount > 0 
                      ? ` ${hiddenSheetsCount} hidden sheet${hiddenSheetsCount !== 1 ? 's' : ''} available.`
                      : ' No hidden sheets found.'
                    }
                  </Text>
                  
                  <View style={styles.hiddenSheetsContainer}>
                    <View style={styles.settingInfo}>
                      <Text style={styles.settingLabel}>Show Hidden Sheets</Text>
                      <Text style={styles.settingDescription}>
                        Display hidden Google Sheets tabs in the races list with visual indicators
                      </Text>
                    </View>
                    <Switch
                      value={showHiddenSheets}
                      onValueChange={handleToggleHiddenSheets}
                      disabled={togglingHiddenSheets || hiddenSheetsCount === 0}
                      trackColor={{ false: '#e0e0e0', true: '#ff9800' }}
                      thumbColor={showHiddenSheets ? '#fff' : '#f4f3f4'}
                    />
                  </View>
                  
                  {hiddenSheetsCount > 0 && (
                    <View style={styles.hiddenSheetsInfo}>
                      <Ionicons name="information-circle-outline" size={16} color="#ff9800" />
                      <Text style={styles.hiddenSheetsInfoText}>
                        Hidden sheets appear with an orange border and "Hidden" badge. 
                        Access requires confirmation to prevent accidental usage.
                      </Text>
                    </View>
                  )}
                  
                  {hiddenSheetsCount === 0 && (
                    <View style={styles.noHiddenSheetsInfo}>
                      <Ionicons name="checkmark-circle-outline" size={16} color="#4caf50" />
                      <Text style={styles.noHiddenSheetsInfoText}>
                        No hidden sheets detected in the current Google Sheets document.
                      </Text>
                    </View>
                  )}
                </View>
              )}

              {/* SESSION INFO JWT */}
              <View style={styles.subSection}>
                <Text style={styles.subSectionTitle}>Session Information</Text>
                
                {sessionInfo && (
                  <View style={styles.sessionInfoContainer}>
                    <View style={styles.sessionRow}>
                      <Text style={styles.sessionLabel}>Status:</Text>
                      <View style={[
                        styles.statusBadge, 
                        sessionInfo.isValid ? styles.statusValid : styles.statusExpired
                      ]}>
                        <Text style={[
                          styles.statusText,
                          sessionInfo.isValid ? styles.statusValidText : styles.statusExpiredText
                        ]}>
                          {sessionInfo.isValid ? 'Active' : 'Expired'}
                        </Text>
                      </View>
                    </View>
                    
                    <View style={styles.sessionRow}>
                      <Text style={styles.sessionLabel}>Time Remaining:</Text>
                      <Text style={styles.sessionValue}>
                        {sessionTimeRemaining}
                      </Text>
                    </View>
                    
                    {sessionInfo.expiresAt && (
                      <View style={styles.sessionRow}>
                        <Text style={styles.sessionLabel}>Expires:</Text>
                        <Text style={styles.sessionValue}>
                          {sessionInfo.expiresAt.toLocaleString()}
                        </Text>
                      </View>
                    )}
                  </View>
                )}

                {jwtPayload && (
                  <View style={styles.jwtInfoContainer}>
                    <Text style={styles.jwtInfoTitle}>JWT Token Info:</Text>
                    <View style={styles.sessionRow}>
                      <Text style={styles.sessionLabel}>Admin:</Text>
                      <Text style={[styles.sessionValue, user?.isAdmin && styles.adminText]}>
                        {user?.isAdmin ? 'Yes' : 'No'}
                      </Text>
                    </View>
                    <View style={styles.sessionRow}>
                      <Text style={styles.sessionLabel}>Device ID:</Text>
                      <Text style={styles.sessionValue}>
                        {jwtPayload.device_id?.substring(0, 8)}...
                      </Text>
                    </View>
                    <View style={styles.sessionRow}>
                      <Text style={styles.sessionLabel}>Session ID:</Text>
                      <Text style={styles.sessionValue}>
                        {jwtPayload.session_id?.substring(0, 12)}...
                      </Text>
                    </View>
                    <View style={styles.sessionRow}>
                      <Text style={styles.sessionLabel}>Issued:</Text>
                      <Text style={styles.sessionValue}>
                        {new Date(jwtPayload.iat * 1000).toLocaleTimeString()}
                      </Text>
                    </View>
                  </View>
                )}
                
                <View style={styles.sessionButtonsContainer}>
                  <TouchableOpacity
                    style={styles.sessionButton}
                    onPress={loadSessionDetails}
                    disabled={loadingSessionInfo}
                  >
                    {loadingSessionInfo ? (
                      <ActivityIndicator color="#fff" size="small" />
                    ) : (
                      <Text style={styles.sessionButtonText}>Refresh Session</Text>
                    )}
                  </TouchableOpacity>

                  <TouchableOpacity
                    style={styles.validateButton}
                    onPress={validateToken}
                    disabled={validatingToken}
                  >
                    {validatingToken ? (
                      <ActivityIndicator color="#fff" size="small" />
                    ) : (
                      <Text style={styles.sessionButtonText}>Validate Token</Text>
                    )}
                  </TouchableOpacity>
                </View>
                
                {detailedSessionInfo && (
                  <View style={styles.sessionDetails}>
                    <Text style={styles.sessionDetailsTitle}>Session Details:</Text>
                    <Text style={styles.sessionDetailsText}>
                      Email: {detailedSessionInfo.email}
                    </Text>
                    <Text style={styles.sessionDetailsText}>
                      Sheet ID: {detailedSessionInfo.sheetId}
                    </Text>
                    <Text style={styles.sessionDetailsText}>
                      Admin: {detailedSessionInfo.isAdmin ? 'Yes' : 'No'}
                    </Text>
                    <Text style={styles.sessionDetailsText}>
                      Remaining: {detailedSessionInfo.timeRemainingHours?.toFixed(2)} hours
                    </Text>
                    <Text style={styles.sessionDetailsText}>
                      Token Type: {detailedSessionInfo.tokenType}
                    </Text>
                  </View>
                )}
              </View>

              {/* GOOGLE SHEET SETTINGS */}
              <View style={styles.subSection}>
                <Text style={styles.subSectionTitle}>Google Sheet Connection</Text>
                
                <Text style={styles.label}>Google Sheet ID</Text>
                <TextInput
                  style={[styles.input, styles.disabledInput]}
                  value={config.GOOGLE_SHEET_ID}
                  editable={false}
                  placeholder="Google Sheet ID (configured by admin)"
                  autoCapitalize="none"
                  autoCorrect={false}
                />
                
                <TouchableOpacity
                  style={styles.testButton}
                  onPress={handleTestConnection}
                  disabled={isTesting || isLoading}
                >
                  {isTesting ? (
                    <LoadingScreen message="" variant="inline" style={styles.buttonLoading} />
                  ) : (
                    <Text style={styles.testButtonText}>Test Connection</Text>
                  )}
                </TouchableOpacity>
              </View>

              {/* CACHE MANAGEMENT & DEBUG */}
              <View style={styles.subSection}>
                <Text style={styles.subSectionTitle}>Cache Management & Debug</Text>
                
                <Text style={styles.cacheDescription}>
                  Manage cached data and session debugging tools.
                </Text>
                
                <View style={styles.debugButtonsContainer}>
                  {/* Cache client */}
                  <TouchableOpacity
                    style={styles.clearCacheButton}
                    onPress={handleClearCache}
                    disabled={clearingCache}
                  >
                    {clearingCache ? (
                      <View style={styles.clearCacheLoading}>
                        <ActivityIndicator color="#fff" size="small" />
                        <Text style={styles.clearCacheButtonText}>Clearing...</Text>
                      </View>
                    ) : (
                      <View style={styles.clearCacheButtonContent}>
                        <Ionicons name="trash-outline" size={20} color="#fff" />
                        <Text style={styles.clearCacheButtonText}>Clear Client Cache</Text>
                      </View>
                    )}
                  </TouchableOpacity>

                  {/* 🆕 Cache serveur (admin seulement) */}
                  {isUserAdmin() && (
                    <TouchableOpacity
                      style={styles.adminCacheButton}
                      onPress={handleClearServerCache}
                      disabled={clearingServerCache}
                    >
                      {clearingServerCache ? (
                        <View style={styles.clearCacheLoading}>
                          <ActivityIndicator color="#fff" size="small" />
                          <Text style={styles.clearCacheButtonText}>Clearing...</Text>
                        </View>
                      ) : (
                        <View style={styles.clearCacheButtonContent}>
                          <Ionicons name="server-outline" size={20} color="#fff" />
                          <Text style={styles.clearCacheButtonText}>Clear Server Cache</Text>
                        </View>
                      )}
                    </TouchableOpacity>
                  )}

                  {/* Debug tools */}
                  <TouchableOpacity
                    style={styles.debugButton}
                    onPress={copyDebugInfo}
                  >
                    <Ionicons name="copy-outline" size={20} color="#fff" />
                    <Text style={styles.debugButtonText}>Copy Debug Info</Text>
                  </TouchableOpacity>

                  <TouchableOpacity
                    style={styles.cleanSessionButton}
                    onPress={cleanSession}
                    disabled={cleaningSession}
                  >
                    {cleaningSession ? (
                      <ActivityIndicator color="#fff" size="small" />
                    ) : (
                      <>
                        <Ionicons name="refresh-outline" size={20} color="#fff" />
                        <Text style={styles.debugButtonText}>Clean Session</Text>
                      </>
                    )}
                  </TouchableOpacity>
                </View>

                {/* 🆕 Admin info */}
                {isUserAdmin() && (
                  <View style={styles.adminInfo}>
                    <Ionicons name="shield-checkmark" size={16} color="#4caf50" />
                    <Text style={styles.adminInfoText}>
                      You have administrator privileges. Server cache operations and hidden sheets management are available.
                    </Text>
                  </View>
                )}
              </View>
            </View>
          )}
        </View>
        
        {/* 4. ABOUT */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>About</Text>
          
          <TouchableOpacity
            style={styles.helpButton}
            onPress={handleHelpPress}
          >
            <Ionicons name="help-circle-outline" size={20} color="#fff" />
            <Text style={styles.helpButtonText}>Help & Documentation</Text>
          </TouchableOpacity>
          
          <View style={styles.aboutRow}>
            <Ionicons name="information-circle-outline" size={24} color="#0066cc" />
            <Text style={styles.aboutText}>Version {config.APP_VERSION}</Text>
          </View>
          
          <Text style={styles.copyright}>© Gabriele Fusco / VTM 2025</Text>
        </View>
      </ScrollView>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#f5f5f5',
  },
  contentContainer: {
    paddingHorizontal: 16,
    paddingTop: 16,
    paddingBottom: 32,
  },
  section: {
    backgroundColor: '#fff',
    borderRadius: 8,
    padding: 16,
    marginBottom: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.1,
    shadowRadius: 2,
    elevation: 2,
  },
  sectionTitle: {
    fontSize: 18,
    
    color: '#333',
    marginBottom: 16,
    fontFamily: 'Montserrat_700Bold',
  },
  // 🆕 Styles pour section dépliante
  advancedHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 4,
  },
  advancedTitle: {
    fontSize: 18,
    
    color: '#333',
    fontFamily: 'Montserrat_700Bold',
  },
  advancedContent: {
    marginTop: 16,
  },
  subSection: {
    marginBottom: 24,
    paddingBottom: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#f0f0f0',
  },
  subSectionTitle: {
    fontSize: 16,
    
    color: '#555',
    marginBottom: 12,
    fontFamily: 'Montserrat_600SemiBold',
  },
  // 🆕 Badge admin
  adminBadge: {
    fontSize: 12,
    color: '#4caf50',
    backgroundColor: '#e8f5e8',
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 12,
    fontFamily: 'Montserrat_600SemiBold',
    marginLeft: 8,
  },
  // 🆕 Styles pour section memory
  sectionMemoryContainer: {
    marginBottom: 16,
    paddingTop: 16,
    borderTopWidth: 1,
    borderTopColor: '#e0e0e0',
  },
  clearSectionButton: {
    backgroundColor: '#ff9800',
    borderRadius: 8,
    padding: 12,
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 48,
    marginTop: 8,
  },
  clearSectionButtonContent: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
  },
  clearSectionLoading: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
  },
  clearSectionButtonText: {
    color: '#fff',
    fontSize: 14,
    
    fontFamily: 'Montserrat_600SemiBold',
    marginLeft: 8,
  },
  // 🆕 Styles pour la gestion des onglets cachés
  hiddenSheetsDescription: {
    fontSize: 14,
    color: '#666',
    lineHeight: 18,
    fontFamily: 'Montserrat_400Regular',
    marginBottom: 16,
  },
  hiddenSheetsContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
  },
  hiddenSheetsInfo: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    padding: 12,
    backgroundColor: '#fff3e0',
    borderRadius: 6,
    borderLeftWidth: 3,
    borderLeftColor: '#ff9800',
    marginBottom: 8,
  },
  hiddenSheetsInfoText: {
    fontSize: 12,
    color: '#f57c00',
    marginLeft: 6,
    fontFamily: 'Montserrat_400Regular',
    flex: 1,
    lineHeight: 16,
  },
  noHiddenSheetsInfo: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    padding: 12,
    backgroundColor: '#e8f5e8',
    borderRadius: 6,
    borderLeftWidth: 3,
    borderLeftColor: '#4caf50',
  },
  noHiddenSheetsInfoText: {
    fontSize: 12,
    color: '#2e7d32',
    marginLeft: 6,
    fontFamily: 'Montserrat_400Regular',
    flex: 1,
    lineHeight: 16,
  },
  label: {
    fontSize: 16,
    
    color: '#555',
    marginBottom: 8,
    fontFamily: 'Montserrat_600SemiBold',
  },
  input: {
    backgroundColor: '#f9f9f9',
    borderWidth: 1,
    borderColor: '#ddd',
    borderRadius: 8,
    padding: 12,
    fontSize: 16,
    marginBottom: 16,
    fontFamily: 'Montserrat_400Regular',
  },
  disabledInput: {
    backgroundColor: '#f0f0f0',
    color: '#888',
    borderColor: '#e0e0e0',
  },
  testButton: {
    backgroundColor: '#007acc',
    borderRadius: 8,
    padding: 12,
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 48,
    marginBottom: 16,
  },
  buttonLoading: {
    backgroundColor: 'transparent',
    paddingVertical: 0,
    paddingHorizontal: 0,
  },
  testButtonText: {
    color: '#fff',
    fontSize: 16,
    
    fontFamily: 'Montserrat_600SemiBold',
  },
  cacheDescription: {
    fontSize: 14,
    color: '#666',
    lineHeight: 18,
    fontFamily: 'Montserrat_400Regular',
    marginBottom: 12,
  },
  debugButtonsContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  clearCacheButton: {
    backgroundColor: '#f57c00',
    borderRadius: 8,
    padding: 12,
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 48,
    flex: 1,
    minWidth: 150,
  },
  // 🆕 Bouton admin cache
  adminCacheButton: {
    backgroundColor: '#e91e63',
    borderRadius: 8,
    padding: 12,
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 48,
    flex: 1,
    minWidth: 150,
  },
  debugButton: {
    backgroundColor: '#9c27b0',
    borderRadius: 8,
    padding: 12,
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 48,
    flex: 1,
    minWidth: 100,
    flexDirection: 'row',
  },
  cleanSessionButton: {
    backgroundColor: '#f44336',
    borderRadius: 8,
    padding: 12,
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 48,
    flex: 1,
    minWidth: 100,
    flexDirection: 'row',
  },
  clearCacheButtonContent: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
  },
  clearCacheLoading: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
  },
  clearCacheButtonText: {
    color: '#fff',
    fontSize: 14,
    
    fontFamily: 'Montserrat_600SemiBold',
    marginLeft: 8,
  },
  debugButtonText: {
    color: '#fff',
    fontSize: 12,
    
    fontFamily: 'Montserrat_600SemiBold',
    marginLeft: 6,
  },
  // 🆕 Info admin
  adminInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 12,
    padding: 8,
    backgroundColor: '#e8f5e8',
    borderRadius: 6,
    borderLeftWidth: 3,
    borderLeftColor: '#4caf50',
  },
  adminInfoText: {
    fontSize: 12,
    color: '#2e7d32',
    marginLeft: 6,
    fontFamily: 'Montserrat_400Regular',
    flex: 1,
  },
  sessionInfoContainer: {
    backgroundColor: '#f9f9f9',
    borderRadius: 8,
    padding: 12,
    marginBottom: 16,
  },
  jwtInfoContainer: {
    backgroundColor: '#e3f2fd',
    borderRadius: 8,
    padding: 12,
    marginBottom: 16,
    borderLeftWidth: 3,
    borderLeftColor: '#0066cc',
  },
  jwtInfoTitle: {
    fontSize: 14,
    
    color: '#0066cc',
    marginBottom: 8,
    fontFamily: 'Montserrat_600SemiBold',
  },
  sessionRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  sessionLabel: {
    fontSize: 14,
    color: '#666',
    fontFamily: 'Montserrat_500Medium',
  },
  sessionValue: {
    fontSize: 14,
    color: '#333',
    fontFamily: 'Montserrat_600SemiBold',
    flex: 1,
    textAlign: 'right',
  },
  // 🆕 Texte admin
  adminText: {
    color: '#4caf50',
    
  },
  statusBadge: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
  },
  statusValid: {
    backgroundColor: '#d4edda',
  },
  statusExpired: {
    backgroundColor: '#f8d7da',
  },
  statusText: {
    fontSize: 12,
    fontFamily: 'Montserrat_600SemiBold',
  },
  statusValidText: {
    color: '#155724',
  },
  statusExpiredText: {
    color: '#721c24',
  },
  sessionButtonsContainer: {
    flexDirection: 'row',
    gap: 8,
    marginBottom: 12,
  },
  sessionButton: {
    backgroundColor: '#007acc',
    borderRadius: 8,
    padding: 12,
    alignItems: 'center',
    flex: 1,
  },
  validateButton: {
    backgroundColor: '#4caf50',
    borderRadius: 8,
    padding: 12,
    alignItems: 'center',
    flex: 1,
  },
  sessionButtonText: {
    color: '#fff',
    fontSize: 14,
    
    fontFamily: 'Montserrat_600SemiBold',
  },
  sessionDetails: {
    backgroundColor: '#e3f2fd',
    borderRadius: 6,
    padding: 12,
    borderLeftWidth: 3,
    borderLeftColor: '#0066cc',
  },
  sessionDetailsTitle: {
    fontSize: 14,
    
    color: '#0066cc',
    marginBottom: 8,
    fontFamily: 'Montserrat_600SemiBold',
  },
  sessionDetailsText: {
    fontSize: 12,
    color: '#0066cc',
    marginBottom: 4,
    fontFamily: 'Montserrat_400Regular',
  },
  settingRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
  },
  settingInfo: {
    flex: 1,
    marginRight: 16,
  },
  settingLabel: {
    fontSize: 16,
    
    color: '#333',
    marginBottom: 4,
    fontFamily: 'Montserrat_600SemiBold',
  },
  settingDescription: {
    fontSize: 14,
    color: '#666',
    fontFamily: 'Montserrat_400Regular',
  },
  hapticTypes: {
    marginTop: 8,
  },
  hapticButtonsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginTop: 8,
    gap: 8,
  },
  hapticButton: {
    backgroundColor: '#f0f0f0',
    borderRadius: 8,
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderWidth: 2,
    borderColor: '#e0e0e0',
    minWidth: 80,
    alignItems: 'center',
  },
  hapticButtonSelected: {
    backgroundColor: '#0066cc',
    borderColor: '#0066cc',
  },
  hapticButtonText: {
    fontSize: 14,
    
    color: '#666',
    fontFamily: 'Montserrat_500Medium',
  },
  hapticButtonTextSelected: {
    color: '#fff',
  },
  noteContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 12,
    padding: 8,
    backgroundColor: '#e3f2fd',
    borderRadius: 6,
    borderLeftWidth: 3,
    borderLeftColor: '#0066cc',
  },
  noteText: {
    fontSize: 12,
    color: '#0066cc',
    marginLeft: 6,
    fontFamily: 'Montserrat_400Regular',
    flex: 1,
  },
  userInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 16,
  },
  userAvatar: {
    width: 50,
    height: 50,
    borderRadius: 25,
    marginRight: 16,
  },
  userDetails: {
    flex: 1,
  },
  userName: {
    fontSize: 16,
    
    color: '#333',
    marginBottom: 4,
    fontFamily: 'Montserrat_600SemiBold',
  },
  userEmail: {
    fontSize: 14,
    color: '#666',
    fontFamily: 'Montserrat_400Regular',
    marginBottom: 4,
  },
  // 🆕 Label admin
  adminLabel: {
    fontSize: 12,
    color: '#4caf50',
    
    fontFamily: 'Montserrat_600SemiBold',
  },
  signOutButton: {
    backgroundColor: '#f44336',
    borderRadius: 8,
    padding: 12,
    alignItems: 'center',
  },
  signOutButtonText: {
    color: '#fff',
    fontSize: 16,
    
    fontFamily: 'Montserrat_600SemiBold',
  },
  aboutRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 16,
  },
  aboutText: {
    fontSize: 16,
    color: '#555',
    marginLeft: 12,
    fontFamily: 'Montserrat_400Regular',
  },
  helpButton: {
    backgroundColor: '#0066cc',
    borderRadius: 8,
    padding: 12,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 16,
  },
  helpButtonText: {
    color: '#fff',
    fontSize: 16,
    
    marginLeft: 8,
    fontFamily: 'Montserrat_600SemiBold',
  },
  copyright: {
    fontSize: 14,
    color: '#888',
    textAlign: 'center',
    marginTop: 8,
    fontFamily: 'Montserrat_400Regular',
  },
});

export default SettingsScreen;